// components/company/tabs/CompanyAddresses.jsx
import { useState, Fragment } from "react";
import { Field } from "../../ui/Field";
import { CountrySelect } from "../../ui/FormControls";
import { addCompanyAddress, updateCompanyAddress, deleteCompanyAddress, addPersonAddress } from "../../../api";

const ADDRESS_TYPE_OPTIONS = [
  { value: "registered", label: "Registered office" },
  { value: "mailing", label: "Mailing" },
  { value: "operational", label: "Operational / Principal place" },
  { value: "billing", label: "Billing" },
  { value: "ubo_residence", label: "UBO residence" },
  { value: "other", label: "Other" },
];

export const CompanyAddresses = ({ addresses, roles, countries, countryById, companyId, notify, refetch }) => {
  const [editAddress, setEditAddress] = useState(null);
  const [newAddress, setNewAddress] = useState({
    type: "registered",
    address_text: "",
    country_id: "",
    person_id: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const uboOptions = roles
    .filter((r) => String(r.role).toLowerCase().includes("ubo") && (r.person_id || r.person?.id))
    .map((r) => ({
      id: r.person_id || r.person.id,
      name: r.person_name || r.person?.full_name || [r.first_name || r.person?.first_name, r.last_name || r.person?.last_name].filter(Boolean).join(" "),
    }));

  const addressTypeLabel = (value) => ADDRESS_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || "";

  const countryLabel = (address) => {
    if (!address) return "";

    const id = address.country_id != null ? String(address.country_id) : null;
    const country = id ? countryById.get(id) : null;

    // Prefer ISO2 from map or from backend, uppercased
    const iso2 = (country && country.iso2) || (address.country_iso2 ? String(address.country_iso2) : null);
    if (iso2) return String(iso2).toUpperCase();

    // Then fall back to name (map or backend)
    if (country && country.name) return country.name;
    if (address.country_name) return address.country_name;

    // Last resort: raw id
    return address.country_id != null ? String(address.country_id) : "";
  };

  const validateNewAddress = () => {
    const newErrors = {};
    if (!newAddress.address_text?.trim()) {
      newErrors.address_text = "Address is required";
    }
    if (newAddress.type === "ubo_residence" && !newAddress.person_id) {
      newErrors.person_id = "Please select a UBO for this residence";
    }
    return newErrors;
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    const newErrors = validateNewAddress();
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const countryId = normalizeCountryId(newAddress.country_id);

      if (newAddress.type === "ubo_residence") {
        await addPersonAddress(newAddress.person_id, {
          type: "residence",
          address_text: newAddress.address_text,
          country_id: countryId,
        });
      } else {
        await addCompanyAddress(companyId, {
          type: newAddress.type,
          address_text: newAddress.address_text,
          country_id: countryId,
        });
      }

      setNewAddress({
        type: "registered",
        address_text: "",
        country_id: "",
        person_id: "",
      });
      setErrors({});
      await refetch();
      notify("Address added successfully");
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditAddress = (address) => {
    setEditAddress({
      id: address.id,
      type: address.type || "registered",
      address_text: address.address_text || "",
      country_id: address.country_id ? String(address.country_id) : "",
    });
  };

  const saveAddressEdit = async (e) => {
    e?.preventDefault?.();
    if (!editAddress) return;

    try {
      await updateCompanyAddress(companyId, editAddress.id, {
        type: editAddress.type,
        address_text: editAddress.address_text,
        country_id: normalizeCountryId(editAddress.country_id),
      });
      setEditAddress(null);
      await refetch();
      notify("Address updated successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  const removeAddress = async (addressId) => {
    if (!confirm("Delete this address?")) return;

    try {
      await deleteCompanyAddress(companyId, addressId);
      await refetch();
      notify("Address deleted successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  return (
    <>
      <div className="card table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Address</th>
              <th>Country</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {addresses.map((address) => {
              const editing = editAddress && editAddress.id === address.id;
              return (
                <Fragment key={`addr-${address.id}`}>
                  <tr>
                    <td>{addressTypeLabel(address.type)}</td>
                    <td>{address.address_text}</td>
                    <td>{countryLabel(address)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {!editing && (
                        <>
                          <button type="button" onClick={() => startEditAddress(address)}>
                            Edit
                          </button>
                          <button type="button" className="secondary" onClick={() => removeAddress(address.id)} style={{ marginLeft: 4 }}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {editing && (
                    <tr className="inline-editor-row">
                      <td colSpan={4}>
                        <form onSubmit={saveAddressEdit}>
                          <div className="inline-editor">
                            <label>
                              <span>Type</span>
                              <select value={editAddress.type} onChange={(e) => setEditAddress({ ...editAddress, type: e.target.value })}>
                                {ADDRESS_TYPE_OPTIONS.map(({ value, label }) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label>
                              <span>Country</span>
                              <CountrySelect value={editAddress.country_id} onChange={(val) => setEditAddress({ ...editAddress, country_id: val })} countries={countries} />
                            </label>

                            <label className="span-2">
                              <span>Address</span>
                              <input value={editAddress.address_text} onChange={(e) => setEditAddress({ ...editAddress, address_text: e.target.value })} />
                            </label>
                          </div>

                          <div className="inline-editor__actions">
                            <button type="submit">Save</button>
                            <button type="button" className="secondary" onClick={() => setEditAddress(null)}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {addresses.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center">
                  No addresses
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form className="card form-section" onSubmit={handleAddAddress}>
        <h3>Add address</h3>
        <div className="form-grid">
          <Field label="Type" required>
            <select value={newAddress.type} onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })} required>
              {ADDRESS_TYPE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Country">
            <CountrySelect value={newAddress.country_id} onChange={(val) => setNewAddress({ ...newAddress, country_id: val })} countries={countries} />
          </Field>

          {newAddress.type === "ubo_residence" && (
            <Field label="UBO (person)" required error={errors.person_id}>
              <select value={newAddress.person_id || ""} onChange={(e) => setNewAddress({ ...newAddress, person_id: e.target.value })} required>
                <option value="">— Select UBO —</option>
                {uboOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || `Person #${p.id}`}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>

        <Field label="Address" required error={errors.address_text}>
          <input value={newAddress.address_text} onChange={(e) => setNewAddress({ ...newAddress, address_text: e.target.value })} required />
        </Field>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? "Adding..." : "Add address"}
          </button>
        </div>

        {newAddress.type === "ubo_residence" && (
          <p className="kv" style={{ marginTop: 8 }}>
            <b>Note:</b> UBO residences are stored under the person (not the company). They won't appear in the list above but are available via the person's profile and in export.
          </p>
        )}
      </form>
    </>
  );
};

export const parseArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim().startsWith("[")) {
    try {
      return JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (typeof v === "string") return v.split(",");
  return [];
};

export const toYN = (v) => {
  if (v === "Y" || v === "N" || v === "") return v;
  if (v === 1 || v === true || v === "1") return "Y";
  if (v === 0 || v === false || v === "0") return "N";
  return "";
};

export const normalizeCountryId = (id) => {
  if (id == null) return null;
  return Number(id);
};

export const createCountryMap = (countries) => {
  const map = new Map();
  countries.forEach((country) => {
    if (country.id != null) {
      const numId = Number(country.id);
      const strId = String(country.id);
      map.set(numId, country);
      map.set(strId, country);
    }
  });
  return map;
};
